import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { BookOpen, Settings2, ArrowUp, ArrowDown, Upload, Plus } from "lucide-react";
import AdminEComicsVolume from "./AdminEComicsVolume";

const FILES = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;

export const StatusTag = ({ status }) => (
  <span className={`bb-tag ${status === "published" ? "bb-tag-green" : ""}`}>{status}</span>
);

export default function AdminEComics() {
  const [sub, setSub] = useState("volumes");
  const [selectedVolume, setSelectedVolume] = useState(null);
  const [volumes, setVolumes] = useState([]);
  const [settings, setSettings] = useState({ product_name: "", price: 50, currency: "INR", active: true });
  const [form, setForm] = useState({ volume_number: "", title: "", description: "" });
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    const [v, s] = await Promise.all([
      api.get("/ecomics/admin/volumes").then(r => r.data).catch(() => []),
      api.get("/ecomics/admin/settings").then(r => r.data).catch(() => null),
    ]);
    setVolumes(v);
    if (s) setSettings(s);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try {
      await api.put("/ecomics/admin/settings", {
        product_name: settings.product_name,
        price: parseInt(settings.price || "0", 10),
        currency: settings.currency,
        active: settings.active,
      });
      toast.success("E-Comics settings saved");
    } catch (e) { toast.error(formatError(e)); }
  };

  const createVolume = async () => {
    if (!form.volume_number || !form.title) { toast.error("Volume number and title are required."); return; }
    try {
      await api.post("/ecomics/admin/volumes", {
        volume_number: parseInt(form.volume_number, 10),
        title: form.title,
        description: form.description,
      });
      toast.success("Volume created (Draft)");
      setForm({ volume_number: "", title: "", description: "" });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/ecomics/admin/volumes/${editing.volume_id}`, {
        volume_number: parseInt(editing.volume_number, 10),
        title: editing.title,
        description: editing.description,
      });
      toast.success("Volume updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const setStatus = async (v, status) => {
    try {
      await api.post(`/ecomics/admin/volumes/${v.volume_id}/status`, { status });
      toast.success(`Volume ${status}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const move = async (index, dir) => {
    const other = index + dir;
    if (other < 0 || other >= volumes.length) return;
    const a = volumes[index], b = volumes[other];
    try {
      await api.put("/ecomics/admin/volumes/reorder", { items: [
        { id: a.volume_id, display_order: b.display_order },
        { id: b.volume_id, display_order: a.display_order },
      ]});
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const uploadCover = async (v, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/ecomics/admin/volumes/${v.volume_id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Cover uploaded");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (selectedVolume) {
    return <AdminEComicsVolume volume={selectedVolume} onBack={() => { setSelectedVolume(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="admin-ecomics">
      <div className="flex flex-wrap gap-1 border-b border-[var(--bb-border)]">
        {[["volumes", "Volumes", BookOpen], ["settings", "Settings", Settings2]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setSub(k)} className={`px-4 py-2.5 text-sm inline-flex items-center gap-1.5 ${sub === k ? "border-b-2 border-[var(--bb-terracotta)] text-[var(--bb-terracotta)]" : "text-[var(--bb-muted)]"}`} data-testid={`ecomics-sub-${k}`}>
            <Icon size={13}/> {l}
          </button>
        ))}
      </div>

      {sub === "settings" && (
        <div className="bb-card p-6 md:p-8 max-w-xl" data-testid="ecomics-settings">
          <div className="bb-overline text-[var(--bb-muted)] mb-4">E-COMICS SETTINGS</div>
          <div className="space-y-5">
            <div>
              <label className="bb-overline text-[var(--bb-muted)]">Product name</label>
              <input value={settings.product_name} onChange={e => setSettings({ ...settings, product_name: e.target.value })} className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)]" data-testid="ecomics-setting-name"/>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="bb-overline text-[var(--bb-muted)]">Price (₹)</label>
                <input type="number" min="0" value={settings.price} onChange={e => setSettings({ ...settings, price: e.target.value })} className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] font-serif text-2xl" data-testid="ecomics-setting-price"/>
              </div>
              <div>
                <label className="bb-overline text-[var(--bb-muted)]">Currency</label>
                <input value={settings.currency} onChange={e => setSettings({ ...settings, currency: e.target.value })} className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)]" data-testid="ecomics-setting-currency"/>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!settings.active} onChange={e => setSettings({ ...settings, active: e.target.checked })} data-testid="ecomics-setting-active"/>
              E-Comics active
            </label>
            <p className="text-xs text-[var(--bb-muted)]">Payment integration will use this price in a later phase. No payment is processed yet.</p>
            <button onClick={saveSettings} className="bb-btn-primary" data-testid="ecomics-settings-save">Save settings</button>
          </div>
        </div>
      )}

      {sub === "volumes" && (
        <div className="space-y-5">
          <div className="bb-card p-5">
            <div className="bb-overline text-[var(--bb-muted)] mb-3 flex items-center gap-2"><Plus size={13}/> CREATE VOLUME</div>
            <div className="grid sm:grid-cols-4 gap-3">
              <input type="number" min="1" placeholder="Volume #" value={form.volume_number} onChange={e => setForm({ ...form, volume_number: e.target.value })} className="bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm" data-testid="ecomics-vol-number"/>
              <input placeholder="Volume title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="sm:col-span-2 bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm" data-testid="ecomics-vol-title"/>
              <button onClick={createVolume} className="bb-btn-primary !py-2 text-sm" data-testid="ecomics-vol-create">Create</button>
              <input placeholder="Short description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="sm:col-span-4 bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm" data-testid="ecomics-vol-desc"/>
            </div>
          </div>

          {volumes.length === 0 ? (
            <div className="bb-card p-10 text-center text-sm text-[var(--bb-muted)]" data-testid="ecomics-volumes-empty">No volumes yet. Create Volume 1 above.</div>
          ) : (() => {
            const filtered = statusFilter === "all" ? volumes : volumes.filter(v => v.status === statusFilter);
            return (
            <>
            <div className="flex flex-wrap gap-1" data-testid="ecomics-status-filters">
              {[["all", "All"], ["draft", "Draft"], ["published", "Published"], ["archived", "Archived"]].map(([k, l]) => {
                const count = k === "all" ? volumes.length : volumes.filter(v => v.status === k).length;
                return (
                  <button key={k} onClick={() => setStatusFilter(k)} className={`px-3.5 py-1.5 rounded-full text-xs border transition ${statusFilter === k ? "bg-[var(--bb-terracotta)] text-white border-[var(--bb-terracotta)]" : "border-[var(--bb-border)] text-[var(--bb-muted)] hover:text-[var(--bb-forest)]"}`} data-testid={`ecomics-filter-${k}`}>
                    {l} ({count})
                  </button>
                );
              })}
            </div>
            {filtered.length === 0 ? (
              <div className="bb-card p-8 text-center text-sm text-[var(--bb-muted)]" data-testid="ecomics-filter-empty">No {statusFilter} volumes.</div>
            ) : (
            <div className="space-y-3">
              {filtered.map((v) => { const i = volumes.indexOf(v); return (
                <div key={v.volume_id} className="bb-card p-4" data-testid={`ecomics-volume-row-${v.volume_id}`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="w-14 h-[74px] rounded overflow-hidden bg-[var(--bb-sand)] ring-1 ring-[var(--bb-border)] shrink-0 grid place-items-center">
                      {v.cover_path ? <img src={FILES + v.cover_path} alt="" className="w-full h-full object-cover"/> : <BookOpen size={18} className="text-[var(--bb-muted)]"/>}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-xs text-[var(--bb-muted)]">Volume {v.volume_number} · order {v.display_order} · {v.vachana_count || 0} vachanas</div>
                      <div className="font-medium text-[var(--bb-forest)]">{v.title}</div>
                      <div className="text-xs text-[var(--bb-muted)] line-clamp-1">{v.description}</div>
                    </div>
                    <StatusTag status={v.status}/>
                    <div className="flex items-center gap-2 text-xs">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded border border-[var(--bb-border)] disabled:opacity-30" data-testid={`ecomics-vol-up-${v.volume_id}`}><ArrowUp size={12}/></button>
                      <button onClick={() => move(i, 1)} disabled={i === volumes.length - 1} className="p-1.5 rounded border border-[var(--bb-border)] disabled:opacity-30" data-testid={`ecomics-vol-down-${v.volume_id}`}><ArrowDown size={12}/></button>
                      <label className="bb-link cursor-pointer inline-flex items-center gap-1" data-testid={`ecomics-vol-cover-${v.volume_id}`}>
                        <Upload size={12}/> Cover
                        <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={e => e.target.files[0] && uploadCover(v, e.target.files[0])}/>
                      </label>
                      <button onClick={() => setEditing({ ...v })} className="bb-link" data-testid={`ecomics-vol-edit-${v.volume_id}`}>Edit</button>
                      {v.status !== "published"
                        ? <button onClick={() => setStatus(v, "published")} className="bb-btn-secondary !py-1 !px-3 text-xs" data-testid={`ecomics-vol-publish-${v.volume_id}`}>Publish</button>
                        : <button onClick={() => setStatus(v, "draft")} className="text-[var(--bb-terracotta)] hover:underline" data-testid={`ecomics-vol-unpublish-${v.volume_id}`}>Unpublish</button>}
                      {v.status !== "archived" && <button onClick={() => setStatus(v, "archived")} className="text-[var(--bb-error)] hover:underline" data-testid={`ecomics-vol-archive-${v.volume_id}`}>Archive</button>}
                      {v.status === "archived" && <button onClick={() => setStatus(v, "draft")} className="bb-link" data-testid={`ecomics-vol-restore-${v.volume_id}`}>Restore</button>}
                      <button onClick={() => setSelectedVolume(v)} className="bb-btn-primary !py-1 !px-3 text-xs" data-testid={`ecomics-vol-manage-${v.volume_id}`}>Manage →</button>
                    </div>
                  </div>
                  {editing?.volume_id === v.volume_id && (
                    <div className="mt-4 pt-4 border-t border-[var(--bb-border)] grid sm:grid-cols-4 gap-3">
                      <input type="number" value={editing.volume_number} onChange={e => setEditing({ ...editing, volume_number: e.target.value })} className="bg-transparent border-b border-[var(--bb-border)] py-2 outline-none text-sm" data-testid="ecomics-vol-edit-number"/>
                      <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className="sm:col-span-2 bg-transparent border-b border-[var(--bb-border)] py-2 outline-none text-sm" data-testid="ecomics-vol-edit-title"/>
                      <div className="flex gap-2">
                        <button onClick={saveEdit} className="bb-btn-primary !py-1.5 !px-3 text-xs" data-testid="ecomics-vol-edit-save">Save</button>
                        <button onClick={() => setEditing(null)} className="text-xs text-[var(--bb-muted)] hover:underline">Cancel</button>
                      </div>
                      <input value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} className="sm:col-span-4 bg-transparent border-b border-[var(--bb-border)] py-2 outline-none text-sm" data-testid="ecomics-vol-edit-desc"/>
                    </div>
                  )}
                </div>
              ); })}
            </div>
            )}
            </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
